import type {
  DecisionEngineChannelConfig,
  DecisionEngineChannelType,
  DecisionEnginePreviewRecipientMode,
  UserColumn,
  VariableDB,
} from "@hightouch/lib/customer-data/decision-engine/types";
import type { DestinationType, MessageTagMap } from "./sync-config-helpers";

/**
 * The destination framework category used by the sync engine:
 * - "trigger": Sends individual API requests per row (e.g. Braze campaign trigger, SFMC triggered send)
 * - "event": Sends event payloads that the destination processes (e.g. Klaviyo, Attentive)
 * - "batch": Uploads data in bulk to the destination (e.g. SFMC data extensions)
 */
export type DestinationCategory = "trigger" | "event" | "batch";

/**
 * Allows a value to be either static or derived from the channel config at runtime.
 * Used for fields like `supportsChannelLevelSync` that are static for most destinations
 * but config-dependent for others (e.g. SFMC DE depends on `useLegacyDataExtension`).
 */
export type MaybeConfigDriven<T> =
  | T
  | ((channelConfig: DecisionEngineChannelConfig) => T);

export type GenerateSyncConfigParams = {
  flowId: string;
  channelId: string;
  channelType: DecisionEngineChannelType;
  channelConfig: DecisionEngineChannelConfig;
  isPreview: boolean;
  sendToHightouchUser: boolean;
  previewRecipientMode?: DecisionEnginePreviewRecipientMode;
  campaignId?: string | number;
  resourceId?: string | number;
  messageVariables?: VariableDB[];
  /** Map of message ID to key-value tags. Embedded in the Liquid template for destination payloads. */
  messageTags?: MessageTagMap;
  /** Pre-filtered user columns (channelConfig.userColumns minus message variables). Computed by the dispatcher. */
  userColumns: UserColumn[];
};

/**
 * True for a preview send in registered recipient mode. Registered sends
 * must never create or mutate customer-data records (leads, user profiles,
 * attribute writes); ESP-side bookkeeping keyed by the preview address itself
 * is acceptable (e.g. SFMC's TSD AutoAddSubscribers auto-creating the
 * subscriber for the account address).
 */
export const isRegisteredPreviewSend = ({
  isPreview,
  previewRecipientMode,
}: Pick<
  GenerateSyncConfigParams,
  "isPreview" | "previewRecipientMode"
>): boolean => isPreview && previewRecipientMode === "registered";

/**
 * The channel's registered-preview key, trimmed. The settings UI trims before
 * saving, but the config is also writable via GraphQL directly, never key a
 * send on whitespace. A whitespace-only key resolves to undefined, so keyed
 * branches fall back to email keying.
 */
export const getPreviewRecipientKey = (
  channelConfig: DecisionEngineChannelConfig,
): string | undefined => channelConfig.previewRecipientKey?.trim() || undefined;

export type GetSyncScheduleParams = {
  channelConfig: DecisionEngineChannelConfig;
  schedulingTimezone: string | null;
};

export type SyncSchedule = {
  type: string;
  schedule: { expression: string };
};

export type CreateResourceParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  channelType: string;
  flowName: string;
  messageName: string;
  baseMessageId: string;
  destinationId: string;
  workspaceId: number;
};

export type UpdateResourceSchemaParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  campaignId: string;
  variables: VariableDB[];
  destinationId?: string;
  workspaceId?: number;
};

export type UpdateChannelResourceParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  channelId: string;
  flowId: string;
  channelLabel?: string;
  variables: VariableDB[];
  userColumns: UserColumn[];
};

export type ListResourcesParams = {
  destinationConfig: any;
  destinationId: number;
  workspaceId: number;
  type: string;
  channelConfig: DecisionEngineChannelConfig;
  /** Scopes the listing to a parent resource when the destination supports it
   * (e.g. Marketo emails scoped to a smart campaign's parent program). */
  parentResourceId?: string;
  /** Filters the listing by name when the destination supports server-side
   * search (e.g. SFMC Content Builder assets). Ignored otherwise. */
  search?: string;
};

export type GetResourceParams = {
  destinationConfig: any;
  destinationId: number;
  workspaceId: number;
  resourceId: string;
  type: string;
  channelConfig: DecisionEngineChannelConfig;
};

export type ResolveContentResourceParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  campaignId: string;
};

export type ResolvedContentResource = {
  resourceId: string;
  type: string;
};

export type RenderResourceForUserParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  resourceId: string;
  /** The previewed user's channel identifier value (e.g. email or lead ID). */
  userIdentifier: string;
};

export type RenderedResourceForUser = { html: string } | { error: string };

/**
 * Resolve a `MaybeConfigDriven<T>` value. If it's a function, call it with channelConfig;
 * otherwise return it directly.
 */
export function resolveConfigDriven<T>(
  value: MaybeConfigDriven<T> | undefined,
  channelConfig: DecisionEngineChannelConfig,
  defaultValue: T,
): T {
  if (value === undefined) return defaultValue;
  if (typeof value === "function") {
    return (value as (c: DecisionEngineChannelConfig) => T)(channelConfig);
  }
  return value;
}

/**
 * Which customer-tunable sync knobs the destination's rETL sync honors at
 * runtime, and the bounds for each (DE-3643). Knobs are persisted into
 * `channelConfig.syncConfigOverride`, which is spread over the generated sync
 * config last — so a knob is only meaningful if the rETL destination *reads*
 * that config key at runtime. An omitted knob (or an omitted descriptor
 * entirely) means the knob is rejected at save time.
 */
export type SyncTuningSupport = {
  /**
   * rETL sync `mode` values a customer may override to. Only list modes when
   * AID does not already emit a load-bearing mode for this destination (e.g.
   * Eloqua CDO requires `insert`, MongoDB requires `upsert`) — overriding
   * those would break the sync.
   */
  modes?: readonly string[];
  /**
   * Batch-size knob. `configKey` is the rETL sync config key the destination
   * reads (usually `rowsPerBatch`; MongoDB uses `batchSize`).
   */
  batchSize?: { configKey: "rowsPerBatch" | "batchSize"; max: number };
  /** Parallelization knob; the config key is always `parallelization`. */
  parallelization?: { max: number };
};

/**
 * Resolve a definition's `syncTuning` descriptor for a specific channel.
 * Function form exists for destinations whose knob support depends on the
 * channel (e.g. OneSignal honors batch size only for in-app enrollment).
 */
export function resolveSyncTuningSupport(
  definition: DecisionEngineDestinationDefinition,
  params: {
    channelType: DecisionEngineChannelType;
    channelConfig: DecisionEngineChannelConfig;
  },
): SyncTuningSupport | undefined {
  const value = definition.syncTuning;
  return typeof value === "function" ? value(params) : value;
}

/**
 * Backend definition for a Decision Engine destination variant.
 *
 * Each destination+configType combination (e.g. "sfmc:trigger", "sfmc:dataExtensions")
 * gets its own definition. Only `generateSyncConfig` is required; everything else
 * has safe defaults (true/false/no-op).
 *
 * Definitions are looked up via `getDestinationDefinition(destinationType, channelConfig)`
 * using composite keys: tries `${destinationType}:${channelConfig.type}` first,
 * then falls back to `${destinationType}`.
 */
export interface DecisionEngineDestinationDefinition {
  destinationType: DestinationType;
  category: DestinationCategory;
  enrollmentChannelTypes?: DecisionEngineChannelType[];

  generateSyncConfig: (
    params: GenerateSyncConfigParams,
  ) => Record<string, unknown>;

  syncTuning?:
    | SyncTuningSupport
    | ((params: {
        channelType: DecisionEngineChannelType;
        channelConfig: DecisionEngineChannelConfig;
      }) => SyncTuningSupport | undefined);

  syncStrategy?: {
    /** default: true */
    supportsChannelLevelSync?: MaybeConfigDriven<boolean>;
    /** default: true */
    shouldManageSyncs?: MaybeConfigDriven<boolean>;
    getSyncSchedule?: (params: GetSyncScheduleParams) => SyncSchedule;
  };

  resourceLifecycle?: {
    /** default: false */
    requiresResourceUpdateOnVariableChange?: MaybeConfigDriven<boolean>;
    /** default: false */
    usesChannelLevelResourceUpdate?: MaybeConfigDriven<boolean>;
  };
}

/**
 * Extended definition that includes async resource lifecycle functions.
 * Only used by backend resolvers — never imported by the frontend app.
 */
export interface DecisionEngineBackendDefinition
  extends DecisionEngineDestinationDefinition {
  resourceLifecycle?: DecisionEngineDestinationDefinition["resourceLifecycle"] & {
    createResource?: (params: CreateResourceParams) => Promise<string>;
    updateResourceSchema?: (
      params: UpdateResourceSchemaParams,
    ) => Promise<void>;
    updateChannelResource?: (
      params: UpdateChannelResourceParams,
    ) => Promise<void>;
  };

  resources?: {
    listResources?: (params: ListResourcesParams) => Promise<any[]>;
    getResource?: (params: GetResourceParams) => Promise<any>;
    resolveContentResource?: (
      params: ResolveContentResourceParams,
    ) => Promise<ResolvedContentResource | null>;
    /**
     * For destinations whose templating executes destination-side: the
     * message-preview render-API path uses this to preview without sending.
     */
    renderResourceForUser?: (
      params: RenderResourceForUserParams,
    ) => Promise<RenderedResourceForUser>;
  };
}
